import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { Title, useRedirect } from "react-admin";
import type { Identifier } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { navigateToBaseRoute } from "@/utils/route-utility";

type ProjectCardRow = {
  id: Identifier;
  name?: string | null;
  code?: string | null;
  status?: string | null;
  currency?: string | null;
  estimatedBudget?: number | null;
  totalCollected?: number | null;
  totalSpent?: number | null;
  totalInayat?: number | null;
  takhmeenCommitted?: number | null;
  takhmeenPaid?: number | null;
  karzanPrincipal?: number | null;
  karzanRepaid?: number | null;
  karzanOutstanding?: number | null;
};

const num = (v: unknown): number => {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
};

const money = (amount: unknown, currencyRaw?: string | null) => {
  const cur = String(currencyRaw ?? "INR").trim() || "INR";
  let currency = cur;
  try {
    Intl.NumberFormat("en-IN", { style: "currency", currency: cur }).format(0);
  } catch {
    currency = "INR";
  }
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
    maximumSignificantDigits: 12,
  }).format(num(amount));
};

const StatLine = ({
  label,
  value,
  hint,
  emphasize,
}: {
  label: string;
  value: string;
  hint?: string;
  emphasize?: boolean;
}) => (
  <Box sx={{ py: 0.5 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant={emphasize ? "subtitle1" : "body2"}
        fontWeight={emphasize ? 700 : 600}
        color={emphasize ? "primary.main" : "text.primary"}
        sx={{ fontVariantNumeric: "tabular-nums", textAlign: "right", minWidth: 0 }}
      >
        {value}
      </Typography>
    </Box>
    {hint ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

const ProjectsDashboard = () => {
  const redirect = useRedirect();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ProjectCardRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    callApi({ location: "projects", method: "GET", id: "financial-cards" })
      .then((res) => {
        const body = res.data as { rows?: ProjectCardRow[] };
        if (!cancelled) setRows(body.rows ?? []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Projects" />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Projects
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Budget, money collected, work payments, and other project finance at a glance.
          </Typography>
          <Button variant="contained" onClick={() => redirect("/projects/create")}>
            New project
          </Button>
          <Button sx={{ ml: 1 }} variant="outlined" onClick={() => redirect("/projects")}>
            All projects
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={2}>
          {rows.map((p) => {
            const cur = p.currency;
            const collected = num(p.totalCollected ?? num(p.totalInayat) + num(p.takhmeenPaid));
            const spent = num(p.totalSpent);
            const net = collected - spent;
            const status = String(p.status ?? "").toUpperCase();
            const statusColor =
              status === "ACTIVE"
                ? "success"
                : status === "CLOSED" || status === "COMPLETED"
                  ? "default"
                  : "info";
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={String(p.id)}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", display: "flex", flexDirection: "column" }}
                >
                  <CardContent
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      "&:last-child": { pb: 2 },
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ mb: 0.5, wordBreak: "break-word" }}
                    >
                      {p.name ?? "—"}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        mb: 1,
                      }}
                    >
                      {p.code ? (
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "ui-monospace, monospace" }}
                        >
                          {p.code}
                        </Typography>
                      ) : null}
                      {status ? (
                        <Chip label={status} size="small" color={statusColor} variant="outlined" />
                      ) : null}
                    </Box>

                    <Divider sx={{ my: 0.5 }} />

                    <StatLine label="Budget" value={money(p.estimatedBudget, cur)} emphasize />
                    <StatLine
                      label="Collected"
                      value={money(collected, cur)}
                      hint="Takhmeen receipts + Inayat"
                    />
                    <StatLine
                      label="Work spent"
                      value={money(spent, cur)}
                      hint="Project work payments"
                    />
                    <StatLine
                      label="Net after spend"
                      value={money(net, cur)}
                      hint="Collected minus work payments"
                    />

                    <Divider sx={{ my: 1 }} />

                    <StatLine label="Inayat (total)" value={money(p.totalInayat, cur)} />
                    <StatLine
                      label="Karzan owed"
                      value={money(p.karzanOutstanding, cur)}
                      hint={`Principal ${money(p.karzanPrincipal, cur)} · Repaid ${money(p.karzanRepaid, cur)}`}
                    />
                    <StatLine
                      label="Takhmeen collected"
                      value={money(p.takhmeenPaid, cur)}
                      hint={`Committed ${money(p.takhmeenCommitted, cur)}`}
                    />

                    <Box sx={{ mt: "auto", pt: 1.5 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => navigateToBaseRoute("projects", String(p.id))}
                      >
                        Open
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default ProjectsDashboard;
